import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query';

import { AppError, isAppError } from '@/shared/api';

import { subscriptionKeys } from './queryKeys';
import { emitErrorToast, emitToast } from './toastEvents';

type QueryToastMeta = {
  errorToast?: string | false;
  toastOn?: 'background' | 'always';
};

type MutationToastMeta = {
  errorToast?: string | false;
};

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AppError;
    queryMeta: QueryToastMeta;
    mutationMeta: MutationToastMeta;
  }
}

const DEFAULT_ERROR_MESSAGE = 'Operation failed. Contact support.';
const PAYMENT_REQUIRED_MESSAGE = 'Subscription required to make changes.';
let queryClientRef: QueryClient | null = null;

const invalidateActiveSubscription = (): void => {
  void queryClientRef?.invalidateQueries({ queryKey: subscriptionKeys.active() });
};

const queryKeyStartsWith = (queryKey: QueryKey, prefix: QueryKey): boolean =>
  prefix.every((part, index) => queryKey[index] === part);

const isRetryableKind = (error: unknown): boolean => {
  if (!isAppError(error)) return true;
  switch (error.kind) {
    case 'auth':
    case 'paymentRequired':
    case 'client':
    case 'cancelled':
      return false;
    case 'network':
    case 'timeout':
    case 'server':
    case 'unknown':
    default:
      return true;
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!isAppError(error)) return;
      if (error.kind === 'cancelled') return;
      if (error.kind === 'paymentRequired') {
        if (!queryKeyStartsWith(query.queryKey, subscriptionKeys.active())) {
          invalidateActiveSubscription();
        }
        return;
      }

      const meta = query.meta;
      if (meta?.errorToast === false) return;

      const hasData = query.state.dataUpdatedAt > 0;
      const toastOn = meta?.toastOn ?? 'background';

      if (toastOn === 'background' && !hasData) return;

      const fallback =
        typeof meta?.errorToast === 'string' ? meta.errorToast : DEFAULT_ERROR_MESSAGE;

      emitErrorToast(error, { message: fallback });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (!isAppError(error)) return;
      if (error.kind === 'cancelled') return;
      if (error.kind === 'paymentRequired') {
        invalidateActiveSubscription();
        emitToast({ type: 'info', message: PAYMENT_REQUIRED_MESSAGE });
        return;
      }

      const meta = mutation.meta;
      if (meta?.errorToast === false) return;

      const message =
        typeof meta?.errorToast === 'string' ? meta.errorToast : DEFAULT_ERROR_MESSAGE;

      emitErrorToast(error, { message });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error) => {
        if (!isRetryableKind(error)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

queryClientRef = queryClient;
